import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Box, Typography, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, Chip, CircularProgress, Alert, Tooltip
} from '@mui/material';

const API_URL = process.env.REACT_APP_API_URL || 'https://api.superchargeme.com';

// Domain metadata: human-readable label + description per data_source.* key.
// Order here drives display order. Domains returned by the API but not listed
// here are appended at the end (forward-compatible).
const DOMAIN_META = [
  { key: 'revenue_dtc',          label: 'Revenue — DTC',            description: 'Direct-to-consumer order revenue (Shopify, etc.)' },
  { key: 'revenue_marketplace',  label: 'Revenue — Marketplace',    description: 'Amazon/marketplace order revenue' },
  { key: 'revenue_wholesale',    label: 'Revenue — Wholesale',      description: 'B2B/wholesale revenue' },
  { key: 'ar',                   label: 'Accounts Receivable',      description: 'Pending receivables per gateway/source' },
  { key: 'ap_inventory',         label: 'AP — Inventory bills',     description: 'Bills for purchased inventory' },
  { key: 'ap_opex',              label: 'AP — OpEx',                description: 'Operating expense bills (software, marketing, etc.)' },
  { key: 'inventory_balance',    label: 'Inventory balance',        description: 'On-hand inventory valuation' },
  { key: 'cogs',                 label: 'COGS',                     description: 'Cost of goods sold (per-SKU calc)' },
  { key: 'bank_reconciliation',  label: 'Bank reconciliation',      description: 'Bank-feed → JE matching' },
  { key: 'vat',                  label: 'VAT',                      description: 'Output + input VAT computation' },
  { key: 'payroll',              label: 'Payroll',                  description: 'Salary, contractor, payroll-tax JEs' },
  { key: 'fx',                   label: 'FX revaluation',           description: 'Foreign currency translation rates' },
  { key: 'fixed_assets',         label: 'Fixed assets',             description: 'Capitalisation, depreciation' },
  { key: 'intercompany',         label: 'Intercompany',             description: 'Cross-subsidiary transfers' },
];

const STATUS_META = {
  active:  { label: 'Active',  color: 'success' },
  draft:   { label: 'Draft',   color: 'warning' },
  paused:  { label: 'Paused',  color: 'default' },
  none:    { label: 'Not set', color: 'default' },
};

function formatDate(dt) {
  if (!dt) return '-';
  try { return new Date(dt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }); }
  catch (e) { return String(dt); }
}

export default function DataSources() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const token = () => localStorage.getItem('token');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API_URL}/api/accounting/data-sources`, {
          headers: { Authorization: `Bearer ${token()}` },
        });
        if (cancelled) return;
        if (res.data.success) setRows(res.data.data || []);
        else setError(res.data.error || 'Failed to load data sources');
      } catch (e) {
        if (cancelled) return;
        setError(e.response?.data?.error || e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (loading) return (<Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress /></Box>);
  if (error)   return (<Alert severity="error" sx={{ m: 2 }}>{error}</Alert>);

  // Index rows by domain for ordered display.
  const byDomain = new Map(rows.map(r => [r.domain, r]));
  const ordered = [
    ...DOMAIN_META.map(meta => ({ meta, row: byDomain.get(meta.key) })),
    // Append any unknown domains the API returned for forward-compat.
    ...rows
      .filter(r => !DOMAIN_META.some(m => m.key === r.domain))
      .map(r => ({ meta: { key: r.domain, label: r.domain, description: '' }, row: r })),
  ];

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" sx={{ mb: 1, fontWeight: 600 }}>Data Sources</Typography>
      <Typography variant="body2" sx={{ mb: 3, color: 'text.secondary' }}>
        Which system feeds which accounting domain for this company. Read-only for now —
        editing comes when domain-specific configuration UIs (e.g. CIN7 vendor classification) ship.
      </Typography>

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 600 }}>Domain</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Source</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Notes</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Updated</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {ordered.map(({ meta, row }) => {
              const isMissing = !row;
              const status = row?.status || 'none';
              const statusMeta = STATUS_META[status] || STATUS_META.none;
              return (
                <TableRow key={meta.key} hover>
                  <TableCell>
                    <Tooltip title={meta.description || ''} arrow placement="right">
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>{meta.label}</Typography>
                        <Typography variant="caption" sx={{ color: 'text.secondary', fontFamily: 'monospace' }}>
                          {meta.key}
                        </Typography>
                      </Box>
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    {isMissing
                      ? <Typography variant="body2" sx={{ color: 'text.disabled', fontStyle: 'italic' }}>(not configured)</Typography>
                      : (row.source
                          ? <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{row.source}</Typography>
                          : <Typography variant="body2" sx={{ color: 'text.disabled', fontStyle: 'italic' }}>(empty)</Typography>)}
                  </TableCell>
                  <TableCell>
                    <Chip size="small" label={statusMeta.label} color={statusMeta.color} variant={status === 'active' ? 'filled' : 'outlined'} />
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                      {row?.notes || ''}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                      {row ? formatDate(row.updated_at) : '-'}
                    </Typography>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <Box sx={{ mt: 2 }}>
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          Backed by <code>superchargeme.company_preferences</code> · keys prefixed with <code>data_source.</code>
        </Typography>
      </Box>
    </Box>
  );
}
